import { Brand } from '../../branded-types';
import { z } from 'zod/v4';
import { ELicenseChannelState } from './license.enums';

export type LicenseId = Brand<string, 'licenseId'>;

export const LICENSE_KEYS = {
  Base: 'base',
  DirectSale: 'direct-sale',
  JourneyPlanning: 'journey-planning',
  OnDemand: 'on-demand',
  PaymentProvider: 'payment-provider',
  Subscription: 'subscription',
  UicSecurityPortal: 'uic-security-portal',
  VdvKa: 'vdvka',
  VdvKaMotics: 'vdvka-motics',
  Vouchers: 'vouchers',
} as const;

export type LicenseKey = (typeof LICENSE_KEYS)[keyof typeof LICENSE_KEYS];

export interface ILicenseReference {
  id?: LicenseId;
  key: LicenseKey;
  label: string;
  description: string;
}

export interface ILicense extends ILicenseReference {
  icon: string;
  position: number;
  webState: ELicenseChannelState;
  appState: ELicenseChannelState;
}

const licenseReferenceShape = {
  id: z
    .uuid()
    .transform((s: string) => s as LicenseId)
    .optional(),
  key: z.enum(LICENSE_KEYS),
  label: z.string(),
  description: z.string(),
};

export const LICENSE_SCHEMA: z.ZodType<ILicense> = z.object({
  ...licenseReferenceShape,
  icon: z.string(),
  position: z.int().nonnegative(),
  webState: z.enum(ELicenseChannelState),
  appState: z.enum(ELicenseChannelState),
});

export const LICENSES_SCHEMA: z.ZodType<ILicense[]> = z.array(LICENSE_SCHEMA);

export const DEFAULT_LICENSES: ILicense[] = [
  {
    id: '30ddfeeb-4f36-42cf-b16a-f2e345f499be' as LicenseId,
    key: LICENSE_KEYS.Base,
    label: 'Basis',
    description:
      'Die Basislizens bringt alle Funktionen mit um den Shop zu betreiben.',
    icon: 'settings',
    position: 0,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Inactive,
  },
  {
    id: '2436c823-e434-4bb1-a0d4-80041c34a87d' as LicenseId,
    key: LICENSE_KEYS.DirectSale,
    label: 'Direktverkauf',
    description:
      'Die Direktverkauf Lizenz ermöglicht den Verkauf von Festpreis oder Relations Tickets.',
    icon: 'shopping_cart_checkout',
    position: 1,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Inactive,
  },
  {
    id: '408e0ac3-157e-4a65-a02b-0789b8e9057f' as LicenseId,
    key: LICENSE_KEYS.UicSecurityPortal,
    label: 'UIC-Sicherheitsportal',
    description: '',
    icon: 'verified_user',
    position: 2,
    webState: ELicenseChannelState.Unavailable,
    appState: ELicenseChannelState.Unavailable,
  },
  {
    id: '4fc8cd4c-66d4-40ac-986b-a148e2649cb8' as LicenseId,
    key: LICENSE_KEYS.VdvKa,
    label: 'VDV-KA Ausgabe',
    description: '',
    icon: 'featured_play_list',
    position: 3,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Inactive,
  },
  {
    id: 'a3a67e45-d871-464b-b63b-cfe6d5a3ff8b' as LicenseId,
    key: LICENSE_KEYS.VdvKaMotics,
    label: 'VDV-KA Ausgabe Motics',
    description: '',
    icon: 'mobile_ticket',
    position: 4,
    webState: ELicenseChannelState.Unavailable,
    appState: ELicenseChannelState.Unavailable,
  },
  {
    id: '88a9aa92-2bd7-4c41-bc76-9867d86440c2' as LicenseId,
    key: LICENSE_KEYS.PaymentProvider,
    label: 'Zahlungsdienstleister',
    description: '',
    icon: 'payments',
    position: 5,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Inactive,
  },
  {
    id: 'ca70f89a-8a73-4d94-9116-722a707a946b' as LicenseId,
    key: LICENSE_KEYS.Subscription,
    label: 'Abonnement',
    description:
      'Die Abonnement Lizenz ermöglicht die Beantragung und Verwaltung von Abonnements.',
    icon: 'laps',
    position: 6,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Inactive,
  },
  {
    id: '9db9b86a-5049-409c-9099-2cda076976f1' as LicenseId,
    key: LICENSE_KEYS.JourneyPlanning,
    label: 'Fahrplanauskunft',
    description: '',
    icon: 'departure_board',
    position: 7,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Inactive,
  },
  {
    id: 'f71355ca-badf-4dca-aee5-e4c1e9e07cdd' as LicenseId,
    key: LICENSE_KEYS.OnDemand,
    label: 'Bedarfsverkehr',
    description: '',
    icon: 'hail',
    position: 8,
    webState: ELicenseChannelState.Inactive,
    appState: ELicenseChannelState.Unavailable,
  },
  {
    id: 'cbd27451-2898-4c56-856f-85bd8ac08bf8' as LicenseId,
    key: LICENSE_KEYS.Vouchers,
    label: 'Gutscheine',
    description: '',
    icon: 'percent',
    position: 9,
    webState: ELicenseChannelState.Unavailable,
    appState: ELicenseChannelState.Unavailable,
  },
];
